import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Button, Skeleton, Typography } from "@mui/material";
import { RootState } from "../../state";
import { User } from "../../entities/User";
import UserService from "../../services/userService";
import CustomTabPanel from "../../components/CustomTabPanel/CustomTabPanel";
import FinancialStats from "./FinancialStats";
import NumericalStats from "./NumericalStats";
import CustomToggle, {
  CustomToggleOption,
} from "../../components/CustomToggle/CustomToggle";
import { Dictionary } from "../../types/types";
import PremiumBadgeIcon from "../../img/icons/vip.png";

export enum STAT_TABS {
  FINANCIAL = "financial",
  NUMERICAL = "numerical",
}

const TAB_INDEX: Dictionary<number> = {
  [STAT_TABS.FINANCIAL]: 0,
  [STAT_TABS.NUMERICAL]: 1,
};

interface CollectionStatsProps {
  path: string;
}

const CollectionStats = ({ path }: CollectionStatsProps) => {
  const [user, setUser] = useState<User | undefined>(undefined);
  // Derive selected tab from route to avoid extra state/effects
  const navigate = useNavigate();

  const loginData = useSelector((state: RootState) => state.login);
  const { userId: loggedInUserId, role: loggedInRole } = loginData || {};
  const isPremium = loggedInRole === 2;

  const { userId, subsection } = useParams();
  const paramUserId = userId ? parseInt(userId) : undefined;
  const targetUserId = paramUserId ?? loggedInUserId;
  const isOwnPage = targetUserId === loggedInUserId;

  // Stabilize service reference and track last fetched id
  const serviceRef = useRef(UserService());
  const getUserDetails = serviceRef.current.getUserDetails;
  const lastFetchedUserId = useRef<number | null>(null);

  function getTabIndexByValue(value: number): STAT_TABS | undefined {
    for (const key in TAB_INDEX) {
      if (TAB_INDEX[key] === value) {
        return key as STAT_TABS;
      }
    }
    return undefined;
  }

  const handleSelectedTabChange = (newValue: number) => {
    const tabTarget = getTabIndexByValue(newValue);
    if (tabTarget) {
      navigate(path + "/" + tabTarget);
    }
  };

  useEffect(() => {
    if (isOwnPage) {
      if (loginData && user?.userId !== loginData.userId) setUser(loginData);
      lastFetchedUserId.current = loggedInUserId ?? null;
      return;
    }
    if (targetUserId && lastFetchedUserId.current !== targetUserId) {
      lastFetchedUserId.current = targetUserId;
      getUserDetails(targetUserId).then((result) => {
        console.log("get User REsult", result);
        if (result) setUser(result);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwnPage, targetUserId, loggedInUserId]);

  const selectedTab: number = (() => {
    if (!isOwnPage) {
      // For guest view, always show numerical stats (index 1)
      return 1;
    }
    const idx = subsection ? TAB_INDEX[subsection] : 0;
    return typeof idx === "number" ? idx : 0;
  })();

  const toggleOptions: Array<CustomToggleOption> = isOwnPage
    ? [
        { label: "Financial", value: 0 },
        { label: "Numerical", value: 1 },
      ]
    : [{ label: "Numerical", value: 1 }];

  const promoMessage = () => {
    return (
      <Box
        className="shadow-box"
        sx={{
          borderRadius: "10px",
          backgroundColor: "white",
          padding: "24px",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "center" },
          justifyContent: "space-between",
          gap: "16px",
          minHeight: "120px",
          marginY: "1.5rem",
        }}
      >
        <Typography sx={{ fontSize: { xs: "14px", md: "18px" } }}>
          This user is a VIP and has chosen to make his stats page invisible to
          others. Would you like to become a VIP too?
        </Typography>
        <Button
          sx={{
            background: "linear-gradient(60deg, #e6c305 0%, #f4ee32 100%)",
            color: "black",
            width: { xs: "100%", md: "160px" },
            fontWeight: 600,
            alignSelf: { xs: "stretch", md: "center" },
          }}
          onClick={() => (window.location.href = "/upgrade-membership")}
        >
          Upgrade
          <img
            loading="lazy"
            width="20"
            src={PremiumBadgeIcon}
            alt=""
            style={{ display: "block", width: "25px", marginLeft: "5px" }}
          />
        </Button>
      </Box>
    );
  };

  return (
    <>
      {isOwnPage && (
        <Box
          className="shadow-box"
          sx={{
            paddingBottom: "12px",
            paddingTop: "12px",
            borderRadius: "10px",
            backgroundColor: "white !important",
            minHeight: "50px",
            paddingLeft: "16px",
            display: { xs: "block", md: "flex" },
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <CustomToggle
            setValue={handleSelectedTabChange}
            value={selectedTab}
            options={toggleOptions}
          />
          {!isPremium && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "20px",
                marginRight: "1rem",
                marginTop: { xs: "10px", md: "0px" },
              }}
            >
              <Typography sx={{ fontSize: { xs: "12px", md: "16px" } }}>
                All stats are for your first 10 shirts only
              </Typography>
              <Button
                sx={{
                  background:
                    "linear-gradient(60deg, #e6c305 0%, #f4ee32 100%)",
                  color: "black",
                  width: "120px",
                  fontWeight: 600,
                }}
                onClick={() => (window.location.href = "/upgrade-membership")}
              >
                Upgrade
                <img
                  loading="lazy"
                  width="20"
                  src={PremiumBadgeIcon}
                  alt=""
                  style={{ display: "block", width: "25px", marginLeft: "5px" }}
                />
              </Button>
            </Box>
          )}
        </Box>
      )}
      {selectedTab !== undefined && (
        <Box
          className="shadow-box"
          sx={{
            borderRadius: "10px",
            backgroundColor: "white",
            marginBottom: "1.5rem",
          }}
        >
          <CustomTabPanel value={selectedTab} index={0}>
            {user?.userId && isOwnPage ? (
              user.userData?.view_financial_stats || isOwnPage ? (
                <FinancialStats userId={user.userId} />
              ) : (
                promoMessage()
              )
            ) : (
              <Skeleton variant="rectangular" height={500} animation="wave" />
            )}
          </CustomTabPanel>
          <CustomTabPanel value={selectedTab} index={1}>
            {user?.userId ? (
              isOwnPage || user.userData?.view_numerical_stats ? (
                <NumericalStats userId={user.userId} />
              ) : (
                promoMessage()
              )
            ) : (
              <Skeleton variant="rectangular" height={500} animation="wave" />
            )}
          </CustomTabPanel>
        </Box>
      )}
    </>
  );
};

export default CollectionStats;
